import { createContext, type MouseEvent, type PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'

interface RouterValue {
  pathname: string
  navigate: (to: string, replace?: boolean) => void
}

const RouterContext = createContext<RouterValue | null>(null)

export function RouterProvider({ children }: PropsWithChildren) {
  const [pathname, setPathname] = useState(window.location.pathname)

  useEffect(() => {
    const update = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', update)
    return () => window.removeEventListener('popstate', update)
  }, [])

  const value = useMemo<RouterValue>(() => ({
    pathname,
    navigate: (to, replace = false) => {
      window.history[replace ? 'replaceState' : 'pushState']({}, '', to)
      setPathname(window.location.pathname)
    },
  }), [pathname])

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

export function useRouter() {
  const context = useContext(RouterContext)
  if (!context) throw new Error('useRouter must be used inside RouterProvider')
  return context
}

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
}

export function Link({ to, onClick, ...props }: LinkProps) {
  const { navigate } = useRouter()
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || props.target) return
    event.preventDefault()
    navigate(to)
  }
  return <a href={to} onClick={handleClick} {...props} />
}

interface NavLinkProps extends LinkProps {
  end?: boolean
}

export function NavLink({ to, end = false, className, ...props }: NavLinkProps) {
  const { pathname } = useRouter()
  const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`)
  const classes = [className, active ? 'active' : ''].filter(Boolean).join(' ')
  return <Link to={to} className={classes} aria-current={active ? 'page' : undefined} {...props} />
}
